/* External */
import {
  memo, useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import InteractiveElement from '@dtvgo/interactive-element';

import { getCommunicationCarousel } from '../../services/communicationCarousel';
import withInteractiveFocusable from '../../hocs/withInteractiveFocusable.hoc';
import useOnLeftArrowPress from '../../hooks/useOnLeftArrowPress';
import { useIsSidebarExpanded, useOpenSidebar } from '../../providers/SidebarStateContext';
import { selectShowModal } from '../../storage/modal/selectors';
import useImpressionOnVisible from '../../hooks/useImpressionOnVisible';
import { useGtmLocalContext } from '../../providers/GtmLocalProvider/GtmLocalProvider';
import {
  GTM_CATEGORIES,
  GTM_SUBCATEGORIES,
  GTM_EVENT_NAMES,
  getParentLander,
  getTagEventFromRoute,
} from '../../utils/googleTags';

import { mapHomeBannerPayload } from '../HomeBannerSlot/mapHomeBannerPayload';
import { navigateHomeBannerCta } from '../HomeBannerSlot/navigateHomeBannerCta';

import './ToastBannerSlot.scss';

const PLACEHOLDER_ASSET = { __toastBannerPlaceholder: true };
const TOAST_SLOT_BASE_CLASS = 'dtv-html5-c-toast-banner-slot';
const TOAST_SLOT_CLASS_SOLID = `${TOAST_SLOT_BASE_CLASS}--solid`;
const TOAST_SLOT_CLASS_WITH_IMAGE = `${TOAST_SLOT_BASE_CLASS}--with-image`;
const TOAST_SLOT_IN_LANDER_HERO_STACK_CLASS = `${TOAST_SLOT_BASE_CLASS}--in-lander-hero-stack`;

const bannerShape = PropTypes.shape({
  title: PropTypes.string,
  logoUrl: PropTypes.string,
  backgroundUrl: PropTypes.string,
  backgroundColor: PropTypes.string,
  ctaText: PropTypes.string,
  ctaLink: PropTypes.string,
});

function ToastBannerSlotFocusableInner({
  banner,
  focused,
  onMouseDown = undefined,
  logoFailed,
  onLogoError,
  inLanderHeroStack = false,
  stealFocusByDefault = false,
  setFocus = undefined,
  domRef = undefined,
}) {
  const isSidebarExpanded = useIsSidebarExpanded();
  const isModalShown = useSelector(selectShowModal) || false;
  const hasAttemptedFocusStealRef = useRef(false);

  useEffect(() => {
    if (!stealFocusByDefault || hasAttemptedFocusStealRef.current) {
      return;
    }

    if (isSidebarExpanded || isModalShown) {
      return;
    }

    hasAttemptedFocusStealRef.current = true;
    setFocus?.();
  }, [stealFocusByDefault, isSidebarExpanded, isModalShown, setFocus]);

  const hasImage = Boolean(banner.backgroundUrl);
  const hasLogo = Boolean(banner.logoUrl) && !logoFailed;

  const containerStyle = {};
  if (hasImage) {
    containerStyle.backgroundImage = `url(${banner.backgroundUrl})`;
  } else if (banner.backgroundColor) {
    containerStyle.backgroundColor = banner.backgroundColor;
  }

  const sectionClass = [
    TOAST_SLOT_BASE_CLASS,
    hasImage ? TOAST_SLOT_CLASS_WITH_IMAGE : TOAST_SLOT_CLASS_SOLID,
    focused ? 'dtv-html5-c-toast-banner-slot--focused' : '',
    inLanderHeroStack ? TOAST_SLOT_IN_LANDER_HERO_STACK_CLASS : '',
  ].filter(Boolean).join(' ');

  const contentClass = [
    'dtv-html5-c-toast-banner-slot__content',
    hasImage ? 'dtv-html5-c-toast-banner-slot__content--with-image' : 'dtv-html5-c-toast-banner-slot__content--solid',
  ].join(' ');

  return (
    <InteractiveElement
      ref={domRef}
      tag="section"
      className={sectionClass}
      style={containerStyle}
      onMouseDown={onMouseDown}
      aria-label={banner.title || 'Toast banner'}
    >
      {hasImage && <div className="dtv-html5-c-toast-banner-slot__overlay" aria-hidden="true" />}
      <div className={contentClass}>
        {hasLogo && (
          <img
            loading="lazy"
            decoding="async"
            src={banner.logoUrl}
            alt=""
            className="dtv-html5-c-toast-banner-slot__logo"
            onError={onLogoError}
          />
        )}
        {banner.title && <h3 className="dtv-html5-c-toast-banner-slot__title">{banner.title}</h3>}
      </div>
    </InteractiveElement>
  );
}

ToastBannerSlotFocusableInner.propTypes = {
  banner: bannerShape.isRequired,
  focused: PropTypes.bool.isRequired,
  onMouseDown: PropTypes.func,
  logoFailed: PropTypes.bool.isRequired,
  onLogoError: PropTypes.func.isRequired,
  inLanderHeroStack: PropTypes.bool,
  stealFocusByDefault: PropTypes.bool,
  setFocus: PropTypes.func,
  domRef: PropTypes.func,
};

const ToastBannerSlotFocusable = memo(
  withInteractiveFocusable()(memo(ToastBannerSlotFocusableInner)),
);

function ToastBannerSlot({
  carouselId,
  focusKey = undefined,
  onFetched,
  onUnavailable = undefined,
  fetchEnabled = true,
  inLanderHeroStack = false,
  stealFocusByDefault = false,
  onBecameFocused = undefined,
}) {
  const intl = useIntl();
  const { commonTaggingEvent } = useGtmLocalContext();
  const goToSideBar = useOpenSidebar();
  const handleLeftArrowPress = useOnLeftArrowPress(goToSideBar);
  const { pathname } = useLocation();
  const tagFromRoute = getTagEventFromRoute({ path: pathname });
  const landerTab = getParentLander(tagFromRoute) || tagFromRoute;
  const onFetchedRef = useRef(onFetched);
  const onUnavailableRef = useRef(onUnavailable);
  const [banner, setBanner] = useState();
  const [logoFailed, setLogoFailed] = useState(false);

  onFetchedRef.current = onFetched;
  onUnavailableRef.current = onUnavailable;

  const handleLogoError = useCallback(() => {
    setLogoFailed(true);
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (!fetchEnabled) {
      queueMicrotask(() => {
        if (!cancelled) onFetchedRef.current([PLACEHOLDER_ASSET]);
      });
      return () => {
        cancelled = true;
      };
    }

    setBanner();

    (async () => {
      const raw = await getCommunicationCarousel({ carouselId });
      if (cancelled) return;
      const mapped = mapHomeBannerPayload(raw);
      // eslint-disable-next-line unicorn/no-null -- ver comentário acima
      setBanner(mapped ?? null);
      if (!mapped && String(carouselId || '').trim()) {
        onUnavailableRef.current?.(carouselId);
      }
      onFetchedRef.current([PLACEHOLDER_ASSET]);
    })();

    return () => {
      cancelled = true;
    };
  }, [carouselId, fetchEnabled]);

  const bannerRef = useImpressionOnVisible({
    enabled: Boolean(banner),
    resetKey: carouselId,
    onImpression: () => {
      commonTaggingEvent({
        category: GTM_CATEGORIES.HOME_DATA,
        subCategory: GTM_SUBCATEGORIES.PAGES.DIALOG_BANNER,
        eventName: GTM_EVENT_NAMES.DIALOG_BANNER.PAGE,
        data: { bannerTitle: banner.title, tab: landerTab },
      });
    },
  });

  const handleEnterPress = useCallback(() => {
    if (!banner?.ctaLink) return;
    commonTaggingEvent({
      category: GTM_CATEGORIES.HOME_DATA,
      subCategory: GTM_SUBCATEGORIES.PAGES.DIALOG_BANNER,
      eventName: GTM_EVENT_NAMES.DIALOG_BANNER.BANNER_CLICK,
      data: { bannerTitle: banner?.title, tab: landerTab },
    });
    navigateHomeBannerCta(banner.ctaLink);
  }, [banner, commonTaggingEvent, landerTab]);

  if (banner === undefined && fetchEnabled && String(carouselId || '').trim()) {
    return (
      <section
        className={[
          TOAST_SLOT_BASE_CLASS,
          TOAST_SLOT_CLASS_SOLID,
          'dtv-html5-c-toast-banner-slot--placeholder',
          inLanderHeroStack ? TOAST_SLOT_IN_LANDER_HERO_STACK_CLASS : '',
        ].filter(Boolean).join(' ')}
        aria-busy="true"
        aria-label={intl.formatMessage({ id: 'common.loadingCommunication' })}
      />
    );
  }

  if (!banner) {
    // eslint-disable-next-line unicorn/no-null -- React requires null for empty render
    return null;
  }

  const resolvedFocusKey = focusKey || `toast-banner-${carouselId}`;

  return (
    <ToastBannerSlotFocusable
      focusKey={resolvedFocusKey}
      onEnterPress={handleEnterPress}
      onArrowPress={handleLeftArrowPress}
      onBecameFocused={onBecameFocused}
      banner={banner}
      logoFailed={logoFailed}
      onLogoError={handleLogoError}
      inLanderHeroStack={inLanderHeroStack}
      stealFocusByDefault={stealFocusByDefault}
      domRef={bannerRef}
    />
  );
}

ToastBannerSlot.propTypes = {
  carouselId: PropTypes.string.isRequired,
  focusKey: PropTypes.string,
  onFetched: PropTypes.func.isRequired,
  onUnavailable: PropTypes.func,
  fetchEnabled: PropTypes.bool,
  inLanderHeroStack: PropTypes.bool,
  stealFocusByDefault: PropTypes.bool,
  onBecameFocused: PropTypes.func,
};

export default memo(ToastBannerSlot);
